import type { PlanDraftOwner } from './consult-autosave';
import { useConsultationWithheld } from './ConsultationPresentation';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchCaseSheetConsents,
  generateTreatmentConsentApi,
  signTreatmentConsentDigitalApi,
  supersedeTreatmentConsentApi,
  type CaseSheetConsentsWire,
  type ClinicalPlanItemView,
  type ConsultPatientBanner,
  type TreatmentConsentView,
  type TreatmentConsentVoidReasonView,
} from '@/dashboard/api';
import {
  AcBadge,
  AcButton,
  acButtonClass,
  ConfirmDialog,
  ModalShell,
  SeamChip,
  SectionShell,
} from '@/dashboard/ui';
import ClinicalConsentCapture, { ExistingConsentCapture } from './ClinicalConsentCapture';
import {
  acceptedPlanLines,
  CONSENT_ENGINE_PENDING_COPY,
  CONSENT_VOID_REASON_OPTIONS,
  consentPrintPath,
  consentSignerLine,
  consentStateLabel,
  consentStateTone,
  consentVoidReasonLabel,
  defaultConsentSelection,
  planLineConsentOwed,
  signatureBase64FromDataUrl,
  type ConsentHeaderChip,
} from '@/dashboard/doctor/consult-consent-view';
import { consultErrorMessage, type HandoffRosterEntry } from '@/dashboard/doctor/doctor-consult-view';
import type { ToothEntryContext } from './tooth-diagram-view';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';

/**
 * DOCTOR-OS-EXPERIENCE-V1 (B3) -- THE CONSENT SURFACE: the treatment-consent
 * document engine's doctor console, mounted in the Treatment-plan column
 * under the plan items.
 *
 *   - DERIVED, NEVER STORED: "Consent owed" chips derive from the server's
 *     uncoveredAcceptedItemIds; a generated draft is progress, never
 *     permission (signed coverage gates completion server-side).
 *   - GENERATE: accepted lines only (B3) -- the modal defaults to the
 *     uncovered accepted set; signer patient|guardian (guardian names the
 *     relationship); witness + notes optional.
 *   - SIGN: on-device signature pad (plain canvas, pointer events) -> the
 *     bare base64 PNG -> POST sign-digital. Print-for-signature opens the
 *     consent print page in a new tab. Attach-signed-scan reuses THE
 *     EvidenceCaptureDialog (fixedClass 'consent_scan'); the exact returned evidence ID rides POST attach-scan. Lost upload
 *     receipts recover only unique matching immutable bytes on this visit;
 *     lost links require independent consent/evidence readback.
 *   - CORRECTIONS: a signed artifact is IMMUTABLE -- void-supersede only
 *     (controlled reasons); an unsigned draft voids without a successor.
 *   - NO WALL OF LEGAL TEXT INLINE: the authorisation wording renders ONLY
 *     on the print sheet (LEGAL TEXT LAW).
 *   - PRE-APPLY HONESTY: available=false renders one SeamChip line.
 */

export interface ConsentPanelProps {
  readonly draftOwner?: PlanDraftOwner;
  readonly clinicId?: number;
  readonly refreshKey?: number;
  readonly embedded?: boolean;
  readonly presentation?: 'v14g';
  readonly appointmentId?: number;
  readonly caseSheetId: number | null;
  readonly sheetStatus: 'draft' | 'finalized' | null;
  readonly planItems: readonly ClinicalPlanItemView[];
  readonly clinicDoctors?: readonly HandoffRosterEntry[];
  readonly toothDisplay?: ToothEntryContext['display'];
  /** Tier-2 evidence flag (me.clinicalEvidenceEnabled): gates ONLY the
   *  attach-signed-scan door (the physical path rides clinical_evidence). */
  readonly evidenceEnabled: boolean;
  /** Banner identity for the print-page opener (the doctor's own lawful
   *  view; contact digits never ride -- they are not on this wire at all). */
  readonly patient: ConsultPatientBanner;
  /** Fired with every (re)loaded wire so the snapshot header derives its
   *  consent chip from the SAME read (one fetch, two renders). */
  readonly onWireChange?: (wire: CaseSheetConsentsWire | null) => void;
  /** DOCTOR-COCKPIT-VISUAL-CONFORM-V1 round 2 (coverage C26): the card head
   *  wears the SAME owed/signed chip the masthead derives from this panel's
   *  own mirrored wire (one derivation, two renders). Absent = round-1
   *  header, byte-identical. */
  readonly headerChip?: ConsentHeaderChip | null;
  /** ROUND 2 (coverage C28): the imaging index line ("X-rays & images - n on
   *  file"), composed by the page from the evidence panel's mirrored count.
   *  null = flag off / read not settled -> the line is ABSENT (never "0"
   *  invented here). */
  readonly evidenceLine?: string | null;
}

type PanelState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; text: string }
  | { kind: 'ready'; wire: CaseSheetConsentsWire };

type DialogState =
  | { kind: 'none' }
  | { kind: 'generate' }
  | { kind: 'sign'; consent: TreatmentConsentView }
  | { kind: 'attach-scan'; consent: TreatmentConsentView }
  | { kind: 'void'; consent: TreatmentConsentView }
  | { kind: 'supersede'; consent: TreatmentConsentView };

// --- The generate modal -------------------------------------------------------

function GenerateConsentDialog({
  caseSheetId,
  planItems,
  wire,
  onGenerated,
  onClose,
}: {
  readonly caseSheetId: number;
  readonly planItems: readonly ClinicalPlanItemView[];
  readonly wire: CaseSheetConsentsWire;
  readonly onGenerated: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const accepted = acceptedPlanLines(planItems);
  const [selected, setSelected] = useState<readonly number[]>(() =>
    defaultConsentSelection(planItems, wire),
  );
  const [signerType, setSignerType] = useState<'patient' | 'guardian'>('patient');
  const [signerName, setSignerName] = useState('');
  const [guardianRelationship, setGuardianRelationship] = useState('');
  const [witnessName, setWitnessName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: number): void => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
    );
  };

  const canGenerate =
    selected.length > 0 &&
    (signerType !== 'guardian' || guardianRelationship.trim() !== '');

  const onGenerate = (): void => {
    if (!canGenerate || busy) return;
    setBusy(true);
    setError(null);
    generateTreatmentConsentApi(caseSheetId, {
      treatmentPlanItemIds: selected,
      signerType,
      ...(signerName.trim() !== '' ? { signerName: signerName.trim() } : {}),
      ...(signerType === 'guardian'
        ? { guardianRelationship: guardianRelationship.trim() }
        : {}),
      ...(witnessName.trim() !== '' ? { witnessName: witnessName.trim() } : {}),
      ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
    })
      .then(() => {
        onGenerated();
      })
      .catch((err: unknown) => {
        setError(consultErrorMessage(err).text);
        setBusy(false);
      });
  };

  return (
    <ModalShell
      eyebrow="Consent"
      title="Generate consent"
      onClose={onClose}
      closeOnScrim={!busy}
      closeOnEscape={!busy}
      closeDisabled={busy}
      testId="consent-generate-dialog"
      footer={
        <div className="ac-modal__footer">
          <AcButton size="sm" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </AcButton>
          <AcButton
            size="sm"
            variant="primary"
            disabled={busy || !canGenerate}
            onClick={onGenerate}
            data-testid="consent-generate-confirm"
          >
            {busy ? 'Generating...' : 'Generate'}
          </AcButton>
        </div>
      }
    >
      <div className="consent-generate">
        <fieldset className="consent-generate__lines">
          <legend>Accepted treatment lines</legend>
          {accepted.map((item) => (
            <label key={item.id} className="consent-generate__line">
              <input
                type="checkbox"
                checked={selected.includes(item.id)}
                disabled={busy}
                onChange={() => toggle(item.id)}
                data-testid={`consent-line-${item.id}`}
              />
              <span>{item.procedureName}</span>
              {planLineConsentOwed(item.id, wire) ? (
                <AcBadge tone="pending">Consent owed</AcBadge>
              ) : null}
            </label>
          ))}
        </fieldset>
        <div className="cockpit-plan__add-grid">
          <label className="clinic-workbench__field">
            <span>Signer</span>
            <select
              className="clinic-workbench__input"
              value={signerType}
              disabled={busy}
              onChange={(e) =>
                setSignerType(e.target.value === 'guardian' ? 'guardian' : 'patient')
              }
              data-testid="consent-signer-type"
            >
              <option value="patient">Patient</option>
              <option value="guardian">Guardian</option>
            </select>
          </label>
          {signerType === 'guardian' ? (
            <label className="clinic-workbench__field">
              <span>Guardian relationship</span>
              <input
                type="text"
                className="clinic-workbench__input"
                maxLength={120}
                value={guardianRelationship}
                disabled={busy}
                onChange={(e) => setGuardianRelationship(e.target.value)}
                data-testid="consent-guardian-relationship"
              />
            </label>
          ) : null}
          <label className="clinic-workbench__field">
            <span>Signer name (optional)</span>
            <input
              type="text"
              className="clinic-workbench__input"
              maxLength={120}
              value={signerName}
              disabled={busy}
              onChange={(e) => setSignerName(e.target.value)}
            />
          </label>
          <label className="clinic-workbench__field">
            <span>Witness (optional)</span>
            <input
              type="text"
              className="clinic-workbench__input"
              maxLength={120}
              value={witnessName}
              disabled={busy}
              onChange={(e) => setWitnessName(e.target.value)}
              data-testid="consent-witness"
            />
          </label>
          <label className="clinic-workbench__field">
            <span>Notes (optional)</span>
            <input
              type="text"
              className="clinic-workbench__input"
              maxLength={500}
              value={notes}
              disabled={busy}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
        </div>
        {error !== null ? (
          <p className="consent-panel__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </ModalShell>
  );
}

// --- The on-device signature pad ---------------------------------------------

function SignaturePadDialog({
  consent,
  onSigned,
  onClose,
}: {
  readonly consent: TreatmentConsentView;
  readonly onSigned: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ctxOf = (): CanvasRenderingContext2D | null =>
    canvasRef.current?.getContext('2d') ?? null;

  const pointAt = (e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (canvas === null) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (busy) return;
    const ctx = ctxOf();
    const canvas = canvasRef.current;
    if (ctx === null || canvas === null) return;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    // Ink color = the element's computed text color (--ac-ink via CSS);
    // no hex literal is authored here (token law).
    const ink =
      typeof window !== 'undefined' ? getComputedStyle(canvas).color : '';
    ctx.strokeStyle = ink !== '' ? ink : 'black';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const p = pointAt(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawingRef.current) return;
    const ctx = ctxOf();
    if (ctx === null) return;
    const p = pointAt(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const onPointerUp = (): void => {
    drawingRef.current = false;
  };

  const onClear = (): void => {
    const ctx = ctxOf();
    const canvas = canvasRef.current;
    if (ctx === null || canvas === null) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const onSave = (): void => {
    const canvas = canvasRef.current;
    if (canvas === null || !hasInk || busy) return;
    const base64 = signatureBase64FromDataUrl(canvas.toDataURL('image/png'));
    if (base64 === '') return;
    setBusy(true);
    setError(null);
    signTreatmentConsentDigitalApi(consent.id, { signatureBase64: base64 })
      .then(() => onSigned())
      .catch((err: unknown) => {
        setError(consultErrorMessage(err).text);
        setBusy(false);
      });
  };

  return (
    <ModalShell
      eyebrow="Consent"
      title="Sign on device"
      onClose={onClose}
      closeOnScrim={!busy}
      closeOnEscape={!busy}
      closeDisabled={busy}
      testId="consent-sign-dialog"
      footer={
        <div className="ac-modal__footer">
          <AcButton size="sm" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </AcButton>
          <AcButton
            size="sm"
            variant="secondary"
            onClick={onClear}
            disabled={busy || !hasInk}
            data-testid="consent-sign-clear"
          >
            Clear
          </AcButton>
          <AcButton
            size="sm"
            variant="primary"
            onClick={onSave}
            disabled={busy || !hasInk}
            data-testid="consent-sign-save"
          >
            {busy ? 'Saving...' : 'Save'}
          </AcButton>
        </div>
      }
    >
      <div className="consent-sign">
        <p className="consent-sign__hint">
          {consent.consentNumber} - the signer draws below. Saving locks the
          document (corrections use void-supersede).
        </p>
        <canvas
          ref={canvasRef}
          className="consent-sign__pad"
          width={640}
          height={220}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          data-testid="consent-sign-pad"
        />
        {error !== null ? (
          <p className="consent-panel__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </ModalShell>
  );
}

// --- One consent card ---------------------------------------------------------

function ConsentCard({
  consent,
  planItems,
  printHref,
  evidenceEnabled,
  sheetFinalized,
  onSign,
  onAttachScan,
  onVoid,
  onSupersede,
}: {
  readonly consent: TreatmentConsentView;
  readonly planItems: readonly ClinicalPlanItemView[];
  readonly printHref: string;
  readonly evidenceEnabled: boolean;
  readonly sheetFinalized: boolean;
  readonly onSign: () => void;
  readonly onAttachScan: () => void;
  readonly onVoid: () => void;
  readonly onSupersede: () => void;
}): JSX.Element {
  const lineNames = consent.treatmentPlanItemIds.map(
    (id) =>
      planItems.find((i) => i.id === id)?.procedureName ?? `Plan line #${id}`,
  );
  const signed =
    consent.state === 'signed_digital' ||
    consent.state === 'signed_physical_captured';
  const voided = consent.state === 'voided_superseded';
  return (
    <li
      className={
        voided ? 'consent-card consent-card--voided' : 'consent-card'
      }
      data-testid={`consent-card-${consent.id}`}
    >
      <div className="consent-card__head">
        <strong className="consent-card__number">{consent.consentNumber}</strong>
        <AcBadge tone={consentStateTone(consent.state)}>
          {consentStateLabel(consent.state)}
        </AcBadge>
        {voided && consent.voidReason !== null ? (
          <span className="consent-card__void-reason">
            {consentVoidReasonLabel(consent.voidReason)}
          </span>
        ) : null}
      </div>
      <dl className="consent-card__facts">
        <div>
          <dt>Treatments</dt>
          <dd>{lineNames.join(', ')}</dd>
        </div>
        <div>
          <dt>Wording</dt>
          <dd>v{consent.templateVersion}</dd>
        </div>
        <div>
          <dt>Doctor</dt>
          <dd>{consent.doctorName ?? 'Unresolved'}</dd>
        </div>
        {consentSignerLine(consent) !== '' ? (
          <div>
            <dt>Signer</dt>
            <dd>{consentSignerLine(consent)}</dd>
          </div>
        ) : null}
        {consent.witnessName !== null ? (
          <div>
            <dt>Witness</dt>
            <dd>{consent.witnessName}</dd>
          </div>
        ) : null}
        <div>
          <dt>{consent.signedAt !== null ? 'Signed' : 'Generated'}</dt>
          <dd>{formatTimestampIst(consent.signedAt ?? consent.createdAt)}</dd>
        </div>
      </dl>
      {consent.state === 'generated' ? (
        <div className="consent-card__actions">
          <AcButton
            size="sm"
            variant="primary"
            onClick={onSign}
            data-testid="consult-consent-sign"
          >
            Sign on device
          </AcButton>
          <a
            className={acButtonClass('secondary', 'sm')}
            href={printHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`consent-print-${consent.id}`}
          >
            Print for signature
          </a>
          {evidenceEnabled ? (
            <AcButton
              size="sm"
              variant="secondary"
              onClick={onAttachScan}
              data-testid={`consent-attach-scan-${consent.id}`}
            >
              Attach signed scan
            </AcButton>
          ) : null}
          <AcButton
            size="sm"
            variant="ghost"
            onClick={onVoid}
            data-testid={`consent-void-${consent.id}`}
          >
            Void
          </AcButton>
        </div>
      ) : null}
      {signed ? (
        <div className="consent-card__actions">
          <a
            className={acButtonClass('secondary', 'sm')}
            href={printHref}
            target="_blank"
            rel="noreferrer"
            data-testid={`consent-print-${consent.id}`}
          >
            Print
          </a>
          {!sheetFinalized ? (
            <AcButton
              size="sm"
              variant="ghost"
              onClick={onSupersede}
              data-testid={`consent-supersede-${consent.id}`}
            >
              Supersede
            </AcButton>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

// --- The panel ----------------------------------------------------------------

export default function ConsentPanel(props: ConsentPanelProps): JSX.Element | null {
  return <ConsentPanelBody key={`${props.caseSheetId}:${props.patient.patientId}`} {...props} />;
}
function ConsentPanelBody({
  caseSheetId,
  clinicId,
  refreshKey,
  appointmentId,
  sheetStatus,
  planItems,
  clinicDoctors,
  toothDisplay,
  evidenceEnabled,
  patient,
  onWireChange,
  draftOwner,
  embedded = false,
  presentation,
  headerChip = null,
  evidenceLine = null,
}: ConsentPanelProps): JSX.Element | null {
  const withheld = useConsultationWithheld();
  const withheldRef = useRef(withheld); withheldRef.current = withheld;
  const [state, setState] = useState<PanelState>({ kind: 'idle' });
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [supersedeReason, setSupersedeReason] =
    useState<TreatmentConsentVoidReasonView>('wording_version_superseded');
  const [actError, setActError] = useState<string | null>(null);
  const [actBusy, setActBusy] = useState(false);
  const onWireChangeRef = useRef(onWireChange);
  onWireChangeRef.current = onWireChange;

  const panelRoot = useRef<HTMLDivElement>(null);
  const live = useRef(true);
  const readGeneration = useRef(0);
  useEffect(() => { live.current = true; return () => { live.current = false; readGeneration.current++; }; }, []);
  const refetch = useCallback(() => {
    if (caseSheetId === null || !live.current || withheldRef.current) return;
    const generation = ++readGeneration.current;
    setState({ kind: 'loading' });
    onWireChangeRef.current?.(null);
    return fetchCaseSheetConsents(caseSheetId)
      .then((wire) => {
        if (!live.current || generation !== readGeneration.current) return;
        setState({ kind: 'ready', wire });
        onWireChangeRef.current?.(wire);
      })
      .catch((err: unknown) => {
        if (!live.current || generation !== readGeneration.current) return;
        setState({ kind: 'error', text: consultErrorMessage(err).text });
        onWireChangeRef.current?.(null);
      });
  }, [caseSheetId, withheld]);

  // The derived owed/covered state depends on the ACCEPTED line set, which
  // the doctor changes IN the adjacent plan section -- the read re-runs
  // whenever that set changes so the derivation never goes stale (the
  // server's uncoveredAcceptedItemIds is the one truth, never re-derived
  // client-side).
  const acceptedSignature = planItems
    .filter((i) => i.status === 'accepted')
    .map((i) => i.id)
    .join(',');
  useEffect(() => {
    readGeneration.current++;
    if (caseSheetId === null || withheld) {
      setState({ kind: 'idle' });
      onWireChangeRef.current?.(null);
      return;
    }
    refetch();
    return () => { readGeneration.current++; };
  }, [caseSheetId, refetch, acceptedSignature, withheld, refreshKey]);

  if (caseSheetId === null) return null;

  const wire = state.kind === 'ready' ? state.wire : null;
  const accepted = acceptedPlanLines(planItems);
  const owedLines =
    wire !== null
      ? accepted.filter((i) => planLineConsentOwed(i.id, wire))
      : [];
  const sheetFinalized = sheetStatus === 'finalized';

  // CONSENT-FOLLOWS-ACCEPTANCE (DOCTOR-JOURNEY-COMPOSE-V1, ordering law 2c):
  // the consent region renders ATTACHED to accepted treatment lines, never as
  // a standing sibling block. With no accepted line and no consent document
  // on record there is nothing to authorise -- the region is ABSENT (clean
  // contextual existence), and it appears the moment an acceptance exists.
  // The consents fetch above stays live (hooks already ran), so the banner
  // consent chip keeps deriving from the same read.
  const consentsOnRecord = wire !== null && wire.consents.length > 0;
  if (presentation !== 'v14g' && accepted.length === 0 && !consentsOnRecord) return null;

  const fireSupersede = (
    consentId: number,
    reason: TreatmentConsentVoidReasonView,
    withoutSuccessor: boolean,
  ): void => {
    setActBusy(true);
    setActError(null);
    supersedeTreatmentConsentApi(consentId, {
      voidReason: reason,
      ...(withoutSuccessor ? { withoutSuccessor: true } : {}),
    })
      .then(() => {
        setDialog({ kind: 'none' });
        refetch();
      })
      .catch((err: unknown) => {
        setActError(consultErrorMessage(err).text);
      })
      .finally(() => setActBusy(false));
  };

  const printHrefFor = (consent: TreatmentConsentView): string =>
    consentPrintPath({
      caseSheetId,
      consentId: consent.id,
      appointmentId,
      patient,
      treatmentNames: consent.treatmentPlanItemIds.map(
        (id) =>
          planItems.find((i) => i.id === id)?.procedureName ??
          `Plan line #${id}`,
      ),
    });

  return (
    <div ref={panelRoot} tabIndex={-1} aria-label="Treatment consent status" data-testid="consult-consent-focus">
    <SectionShell
      embedded={embedded}
      eyebrow="Consent"
      // ROUND 2 (C26): the candidate-b card title -- the SAME grammar the
      // doctor-v2 reference ConsentSection carries ("Consent & evidence").
      // ROUND 3 (rule 3): the helper sub-sentence moved to the title attr
      // of the generate act (the reference draws no helper prose).
      title="Consent & evidence"
      actions={
        presentation !== 'v14g' && headerChip !== null ? (
          <AcBadge tone={headerChip.tone} testId="consult-consent-head-chip">
            {headerChip.label}
          </AcBadge>
        ) : undefined
      }
      testId="consult-consent-panel"
    >
          {presentation === 'v14g' && (accepted.length > 0 || wire?.consents.some(consent=>consent.recordSource==='uploaded_original')) ? <ClinicalConsentCapture draftOwner={draftOwner} key={caseSheetId} purpose="treatment" caseSheetId={caseSheetId} clinicId={clinicId ?? 0} patientLabel={patient.patientName ?? undefined} wire={state.kind === 'ready' ? state.wire : null} planItems={planItems} clinicDoctors={clinicDoctors} toothDisplay={toothDisplay} enabled={evidenceEnabled} onRecorded={() => void refetch()} onRetry={() => void refetch()}/> : null}
      {state.kind === 'loading' || state.kind === 'idle' ? (
        <p className="day-board__loading" role="status">
          Loading consents...
        </p>
      ) : state.kind === 'error' ? (
        <div><p className="day-board__error" role="alert">{state.text}</p><AcButton variant="secondary" onClick={() => {
          void refetch()?.then(() => { if (live.current && panelRoot.current && !panelRoot.current.closest('[hidden]')) panelRoot.current.focus(); });
        }}>Retry consents</AcButton></div>
      ) : !state.wire.available ? (
        presentation === 'v14g' ? <p role="status">Treatment consent is unavailable.</p> : <SeamChip
          label={CONSENT_ENGINE_PENDING_COPY}
          explainer="Treatment consents store nothing until the founder applies migration 20260804_2."
          testId="consult-consent-pending"
        />
      ) : (
        <>
          {presentation !== 'v14g' && owedLines.length > 0 ? (
            <ul className="consent-panel__owed" data-testid="consult-consent-owed">
              {owedLines.map((item) => (
                <li key={item.id} className="consent-panel__owed-line">
                  <span>{item.procedureName}</span>
                  <AcBadge tone="pending" testId={`consent-owed-${item.id}`}>
                    Consent owed
                  </AcBadge>
                </li>
              ))}
            </ul>
          ) : null}
          {accepted.length === 0 ? (
            <p className="clinic-workbench__muted">
              {presentation === 'v14g' ? 'Nothing accepted yet, so no treatment consent is needed.' : 'Consent is generated for accepted treatment lines. Nothing is accepted on this visit yet.'}
            </p>
          ) : null}
          {presentation !== 'v14g' && !sheetFinalized && accepted.length > 0 ? (
            <div className="clinic-workbench__actions">
              <AcButton
                size="sm"
                variant={owedLines.length > 0 ? 'primary' : 'secondary'}
                onClick={() => setDialog({ kind: 'generate' })}
                title="Signed authorisation per accepted procedure - generated here, signed on device or on paper."
                data-testid="consult-consent-generate"
              >
                Generate consent
              </AcButton>
            </div>
          ) : null}
          {state.wire.consents.some(consent => consent.recordSource !== 'uploaded_original') ? (
            <ul className="consent-panel__cards" data-testid="consult-consent-cards">
              {state.wire.consents.filter(consent => consent.recordSource !== 'uploaded_original').map((consent) => (
                <ConsentCard
                  key={consent.id}
                  consent={consent}
                  planItems={planItems}
                  printHref={printHrefFor(consent)}
                  evidenceEnabled={evidenceEnabled}
                  sheetFinalized={sheetFinalized}
                  onSign={() => setDialog({ kind: 'sign', consent })}
                  onAttachScan={() => {
                    setActError(null);
                    setDialog({ kind: 'attach-scan', consent });
                  }}
                  onVoid={() => {
                    setActError(null);
                    setDialog({ kind: 'void', consent });
                  }}
                  onSupersede={() => {
                    setActError(null);
                    setSupersedeReason('wording_version_superseded');
                    setDialog({ kind: 'supersede', consent });
                  }}
                />
              ))}
            </ul>
          ) : null}
          {actError !== null && dialog.kind === 'none' ? (
            <p className="consent-panel__error" role="alert">
              {actError}
            </p>
          ) : null}
        </>
      )}

      {/* ROUND 2 (C28): the imaging index line at the card foot -- composed
          by the page from the evidence panel's own read (never fetched or
          invented here); absent when the flag is off or the read has not
          settled. The full evidence panel below stays the working surface. */}
      {evidenceLine !== null ? (
        <p
          className="consent-panel__evidence-row"
          data-testid="consult-consent-evidence-row"
        >
          {evidenceLine}
        </p>
      ) : null}

      {dialog.kind === 'generate' && wire !== null ? (
        <GenerateConsentDialog
          caseSheetId={caseSheetId}
          planItems={planItems}
          wire={wire}
          onGenerated={() => {
            setDialog({ kind: 'none' });
            refetch();
          }}
          onClose={() => setDialog({ kind: 'none' })}
        />
      ) : null}
      {dialog.kind === 'sign' ? (
        <SignaturePadDialog
          consent={dialog.consent}
          onSigned={() => {
            setDialog({ kind: 'none' });
            refetch();
          }}
          onClose={() => setDialog({ kind: 'none' })}
        />
      ) : null}
      {dialog.kind === 'attach-scan' ? (
        <>
          <ExistingConsentCapture draftOwner={draftOwner}
            caseSheetId={caseSheetId} consent={dialog.consent} patientLabel={patient.patientName ?? undefined}
            onAttached={() => { if (!live.current || withheldRef.current) return; setDialog({ kind: 'none' }); void refetch(); }}
            onClose={() => setDialog({ kind: 'none' })}
          />
          {actError !== null ? (
            <p className="consent-panel__error" role="alert">
              {actError}
            </p>
          ) : null}
        </>
      ) : null}
      {dialog.kind === 'void' ? (
        <ConfirmDialog
          eyebrow="Consent"
          title="Void this consent?"
          destructive
          confirmLabel="Void"
          busyLabel="Voiding..."
          busy={actBusy}
          onConfirm={() =>
            fireSupersede(dialog.consent.id, 'generated_in_error', true)
          }
          onCancel={() => setDialog({ kind: 'none' })}
          confirmTestId="consent-void-confirm"
          testId="consent-void-dialog"
        >
          <p>
            {dialog.consent.consentNumber} stays on record as superseded - it
            never disappears. No signature was captured on it.
          </p>
          {actError !== null ? <p role="alert">{actError}</p> : null}
        </ConfirmDialog>
      ) : null}
      {dialog.kind === 'supersede' ? (
        <ConfirmDialog
          eyebrow="Consent"
          title="Supersede this consent?"
          destructive
          confirmLabel="Supersede"
          busyLabel="Superseding..."
          busy={actBusy}
          onConfirm={() =>
            fireSupersede(dialog.consent.id, supersedeReason, false)
          }
          onCancel={() => setDialog({ kind: 'none' })}
          confirmTestId="consent-supersede-confirm"
          testId="consent-supersede-dialog"
        >
          <p>
            A signed consent is never edited: a replacement document is
            generated over the same lines and {dialog.consent.consentNumber}
            {' '}is retired pointing at it. Both stay on record.
          </p>
          <label className="clinic-workbench__field">
            <span>Reason</span>
            <select
              className="clinic-workbench__input"
              value={supersedeReason}
              onChange={(e) =>
                setSupersedeReason(
                  e.target.value as TreatmentConsentVoidReasonView,
                )
              }
              data-testid="consent-supersede-reason"
            >
              {CONSENT_VOID_REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          {actError !== null ? <p role="alert">{actError}</p> : null}
        </ConfirmDialog>
      ) : null}
    </SectionShell>
    </div>
  );
}
